import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FilterSectionComponent } from '../../../components/filter-section/filter-section.component';
import { FilterService } from '../../../services/filter.service';
import { CommonService } from '../../../services/common.service';
import * as Types from '../../../shared/types';
import { transformTestToFilters, transformColumnPreferences } from '../../../shared/appUtil';
import { SharedDataService } from '../../../services/shared-data.service';
import { OutsideClickDirective } from '../../../directives/outside-click.directive';

/**
 * Wraps the exact Insights dashboard filter-section with a scoped FilterService
 * so the chart form has its own independent filter state.
 * When buttonMode is true, shows a "Filters" button; on click the filter panel opens on top (above the button).
 */
@Component({
  selector: 'chart-filter-section-wrapper',
  standalone: true,
  imports: [CommonModule, FilterSectionComponent, OutsideClickDirective],
  providers: [FilterService],
  template: `
    @if (buttonMode) {
      <div class="chart-filter-section-wrapper chart-filter-section-wrapper--button-mode" [class.chart-filter-section-wrapper--has-filters]="appliedFilterCount > 0">
        <button type="button" class="chart-filter-section-wrapper__btn" (click)="toggleFilterDropdown()"
          [class.chart-filter-section-wrapper__btn--active]="filterDropdownOpen">
          <img src="assets/icons/filter_list.svg" alt="" class="chart-filter-section-wrapper__btn-icon" />
          <span>{{ filterButtonLabel }}</span>
        </button>
        @if (appliedFilterCount > 0) {
          <div class="chart-filter-section-wrapper__summary" aria-live="polite">
            <span class="chart-filter-section-wrapper__summary-badge">{{ appliedFilterCount }} filter{{ appliedFilterCount !== 1 ? 's' : '' }} applied</span>
            @if (appliedFilterSummary) {
              <span class="chart-filter-section-wrapper__summary-text">{{ appliedFilterSummary }}</span>
            }
          </div>
        }
        @if (filterDropdownOpen) {
          <div class="chart-filter-section-wrapper__dropdown" appOutSideClick (outSideClick)="onOutsideClick()">
            <filter-section
              [embedded]="true"
              (filtersApplied)="onFiltersApplied($event)">
            </filter-section>
          </div>
        }
      </div>
    } @else {
      <div class="chart-filter-section-wrapper" [class.chart-filter-section-wrapper--has-filters]="appliedFilterCount > 0">
        @if (appliedFilterCount > 0) {
          <div class="chart-filter-section-wrapper__summary chart-filter-section-wrapper__summary--inline" aria-live="polite">
            <span class="chart-filter-section-wrapper__summary-badge">{{ appliedFilterCount }} filter{{ appliedFilterCount !== 1 ? 's' : '' }} applied</span>
            @if (appliedFilterSummary) {
              <span class="chart-filter-section-wrapper__summary-text">{{ appliedFilterSummary }}</span>
            }
          </div>
        }
        <filter-section
          [embedded]="true"
          (filtersApplied)="onFiltersApplied($event)">
        </filter-section>
      </div>
    }
  `,
  styles: [`
    .chart-filter-section-wrapper {
      display: block;
    }
    .chart-filter-section-wrapper--button-mode {
      position: relative;
      display: block;
    }
    .chart-filter-section-wrapper__btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      min-height: 40px;
      border: 1px solid #e1e5ea;
      border-radius: 6px;
      background: #fff;
      color: #475258;
      font-family: Lexend, sans-serif;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
    }
    .chart-filter-section-wrapper__btn:hover {
      background: #f5f7fa;
      border-color: #c1c9d0;
    }
    .chart-filter-section-wrapper__btn--active {
      background: #f0f0ff;
      border-color: #534AD8;
      box-shadow: 0 0 0 1px #534AD8;
    }
    .chart-filter-section-wrapper__btn-icon {
      width: 18px;
      height: 18px;
      opacity: 0.85;
    }
    .chart-filter-section-wrapper__dropdown {
      position: absolute;
      bottom: 100%;
      left: 0;
      right: 0;
      margin-bottom: 8px;
      min-width: 100%;
      width: 100%;
      max-height: 70vh;
      overflow-y: auto;
      overflow-x: visible;
      background: #fff;
      border: 1px solid #e1e5ea;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
      z-index: 1050;
    }
    .chart-filter-section-wrapper--has-filters {
      padding: 10px 12px;
      margin: 0 -12px;
      border-radius: 8px;
      background: #f0f4ff;
      border-left: 3px solid #534AD8;
    }
    .chart-filter-section-wrapper__summary {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      margin-top: 8px;
      font-size: 13px;
    }
    .chart-filter-section-wrapper__summary--inline {
      margin-top: 0;
      margin-bottom: 8px;
    }
    .chart-filter-section-wrapper__summary-badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      border-radius: 4px;
      background: #534AD8;
      color: #fff;
      font-weight: 600;
    }
    .chart-filter-section-wrapper__summary-text {
      color: #475258;
    }
  `]
})
export class ChartFilterSectionWrapperComponent implements OnInit, OnChanges {
  private readonly filterService = inject(FilterService);
  private readonly commonService = inject(CommonService);
  private readonly sharedDataService = inject(SharedDataService);

  /** When true, show a button; on click the filter panel opens above (like Insights dashboard). */
  @Input() buttonMode: boolean = false;
  /** Initial filters when editing a chart (Insights FilterCondition format from FilterRequest[]). */
  @Input() initialFilterConditions: Types.FilterCondition[] = [];

  filterDropdownOpen = false;
  /** Used so the button label updates when user applies/clears filters. */
  appliedFilterCount = 0;
  /** Human-readable summary of applied conditions (e.g. "Contact Id contains 'dee'"). */
  appliedFilterSummary = '';

  get filterButtonLabel(): string {
    if (this.appliedFilterCount > 0) {
      return this.appliedFilterCount > 1 ? `${this.appliedFilterCount} Filters Applied` : '1 Filter Applied';
    }
    return 'Filters';
  }

  toggleFilterDropdown(): void {
    if (this.filterDropdownOpen) {
      // Closing without Apply — discard draft edits
      this.closeFilterPanelWithoutApplying();
    } else {
      // Opening — sync draft to applied so the panel starts clean
      this.filterService.resetTempFiltersToActual();
      this.filterDropdownOpen = true;
    }
  }

  onOutsideClick(): void {
    this.closeFilterPanelWithoutApplying();
  }

  /** Close panel and discard any unapplied (temp) filter edits. */
  private closeFilterPanelWithoutApplying(): void {
    this.filterService.resetTempFiltersToActual();
    this.filterDropdownOpen = false;
  }

  ngOnInit(): void {
    this.loadFilterOptions();
    this.loadColumnNames();
    // Badge/summary track applied filters only (not draft/temp edits)
    this.filterService.getFilterCondition().subscribe(() => {
      this.updateAppliedFilterState();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilterConditions']) {
      // Always sync — including empty arrays when creating a new chart or clearing filters
      this.filterService.updateFilterData(this.initialFilterConditions ?? []);
      this.updateAppliedFilterState();
    }
  }

  /** Updates applied filter count and summary from applied (not temp) payload. */
  private updateAppliedFilterState(): void {
    const payload = this.getFiltersForPayload();
    this.appliedFilterCount = payload.length;
    this.appliedFilterSummary = this.buildFilterSummary(payload);
  }

  /** Builds a short human-readable summary of applied filters (e.g. "Contact Id contains 'dee'"). */
  private buildFilterSummary(filters: Types.FilterRequest[]): string {
    if (!filters?.length) return '';
    const parts = filters.map((f) => {
      const col = f.columnName || 'Column';
      const op = (f.operationType || '').replace(/_/g, ' ').toLowerCase();
      const val = Array.isArray(f.value) ? f.value.join(', ') : (f.value ?? '');
      const valStr = val ? ` "${val}"` : '';
      return `${col} ${op}${valStr}`.trim();
    });
    return parts.join(' · ');
  }

  private loadColumnNames(): void {
    this.commonService.getUserColumnPreference({}).subscribe({
      next: (names: Array<Types.ColumnPreferenceType>) => {
        this.sharedDataService.setColumnNames(transformColumnPreferences(names));
      },
    });
  }

  private loadFilterOptions(): void {
    const empty = { filters: {} };
    forkJoin({
      insights: this.commonService.fetchDataFiltersInsights({}).pipe(catchError(() => of(empty))),
      clusters: this.commonService.fetchDataFiltersClusters({}).pipe(catchError(() => of(empty))),
      connect: this.commonService.fetchDataFiltersConnectAttributes({}).pipe(catchError(() => of(empty))),
    }).subscribe((result: any) => {
      const merged = { filters: {} } as Types.DataFilters;
      [result.insights, result.clusters, result.connect].forEach((data: any) => {
        if (data?.filters && typeof data.filters === 'object') {
          Object.assign(merged.filters, data.filters);
        }
      });
      this.filterService.updateAllFilters(merged);
    });
  }

  onFiltersApplied(_conditions: Types.FilterCondition[]): void {
    // Close the filter panel and refresh count/summary when user clicks "Apply Changes"
    this.filterDropdownOpen = false;
    this.updateAppliedFilterState();
  }

  /**
   * Returns applied filter conditions in the same format as Insights dashboard
   * (FilterRequest[]). Draft/temp edits are ignored until the user clicks Apply Changes.
   * Use this as request.filters when saving a chart.
   */
  getFiltersForPayload(): Types.FilterRequest[] {
    const applied = this.filterService.getFilterConditionDataValue();
    const { data } = transformTestToFilters(applied);
    return data;
  }
}
